import { useEffect, useState } from "react";
import { Hourglass } from "lucide-react";
import { useT } from "@ec2/i18n/react";
import type { Locale } from "@ec2/i18n";
import { countdownParts, formatEventStart } from "../lib/time";

const pad = (n: number) => String(n).padStart(2, "0");

/**
 * Joined before the event starts: in place of the quest list, a tile shaped
 * like a quest card — but not a button — counting down to the start, saying
 * that every quest appears then. Calls `onStarted` once when it reaches zero,
 * so the screen can fetch the now-visible quests.
 */
export function EventCountdownCard({
  startsAt,
  timeZone,
  locale,
  onStarted,
}: {
  startsAt: Date;
  timeZone: string;
  locale: Locale;
  onStarted: () => void;
}) {
  const t = useT();
  const [now, setNow] = useState(() => new Date());
  const parts = countdownParts(startsAt, now);

  useEffect(() => {
    if (parts.total === 0) {
      onStarted();
      return;
    }
    const timer = window.setTimeout(() => setNow(new Date()), 1000);
    return () => window.clearTimeout(timer);
  }, [now, parts.total === 0]);

  const clock = `${pad(parts.hours)}:${pad(parts.minutes)}:${pad(parts.seconds)}`;
  return (
    <article
      data-testid="ec-countdown-card"
      aria-live="off"
      className="flex w-full flex-col gap-3 rounded-(--radius-card) border border-border bg-surface p-4 text-left"
    >
      <div className="flex items-center gap-3">
        <span className="flex size-10 items-center justify-center rounded-[11px] bg-primary/15 text-primary">
          <Hourglass size={20} aria-hidden />
        </span>
        <span className="font-display text-base font-[650] text-fg">
          {t("ec2.countdown.title")}
        </span>
      </div>
      <p
        className="font-display text-[34px] leading-none font-bold tabular-nums tracking-[-0.02em] text-fg"
        data-testid="ec-countdown-time"
        aria-label={t("ec2.countdown.startsAt", {
          when: formatEventStart(startsAt, locale, timeZone),
        })}
      >
        {parts.days > 0 ? t("ec2.countdown.daysAnd", { days: parts.days, time: clock }) : clock}
      </p>
      <p className="text-sm text-fg-secondary">
        {t("ec2.countdown.body", { when: formatEventStart(startsAt, locale, timeZone) })}
      </p>
    </article>
  );
}
